package one.healthium.insights.data.dto

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.modules.SerializersModule
import kotlinx.serialization.modules.polymorphic
import one.healthium.domain.common.Insets

@Serializable
internal sealed interface ContentEntryDto {

    @Serializable
    @SerialName("title")
    data class Title(
        @SerialName("text") val text: String
    ) : ContentEntryDto

    @Serializable
    @SerialName("image")
    data class Image(
        @SerialName("url") val url: String,
        @SerialName("thumb_url") val thumbUrl: String,
        @SerialName("height") val height: Int? = null,
    ) : ContentEntryDto

    @Serializable
    @SerialName("text")
    data class Text(
        @SerialName("text") val text: String
    ) : ContentEntryDto

    @Serializable
    @SerialName("rich_text")
    data class RichText(
        @SerialName("text") val text: String,
        @SerialName("insets") val insets: Insets? = null,
    ) : ContentEntryDto

    @Serializable
    @SerialName("supplement")
    data class Supplement(
        @SerialName("coach") val coach: String? = null,
        @SerialName("title") val title: String? = null,
        @SerialName("name") val name: String? = null,
        @SerialName("description") val description: String? = null,
        @SerialName("image_url") val imageUrl: String? = null,
        @SerialName("button_title") val buttonTitle: String? = null,
        @SerialName("product_url") val productUrl: String? = null,
    ) : ContentEntryDto

    @Serializable
    @SerialName("day")
    data class Day(
        @SerialName("title") val title: String? = null,
        @SerialName("meals") val meals: List<MealDto>? = null,
    ) : ContentEntryDto

    @Serializable
    @SerialName("card")
    data class Card(
        @SerialName("title") val title: String?,
        @SerialName("image") val image: String?,
        @SerialName("hint") val hint: HintDto? = null,
        @SerialName("action") val action: ActionDto,
    ) : ContentEntryDto

    @Serializable
    @SerialName("group")
    data class Group(
        @SerialName("background_color") val backgroundColor: String? = null,
        @SerialName("corner") val cornerRadius: Int? = null,
        @SerialName("insets") val insets: Insets? = null,
        @SerialName("content") val content: List<ContentEntryDto>,
    ) : ContentEntryDto

    @Serializable
    @SerialName("carousel")
    data class Carousel(
        @SerialName("variant") val variant: String? = null,
        @SerialName("content") val content: List<CardDto>,
    ) : ContentEntryDto

    @Serializable
    @SerialName("header")
    data class Header(
        @SerialName("text") val text: String,
    ) : ContentEntryDto

    @Serializable
    @SerialName("workout_card")
    data class ImageCard(
        @SerialName("title") val title: String,
        @SerialName("thumb_url") val image: String,
        @SerialName("hint") val hint: HintDto?,
        @SerialName("action") val action: ActionDto,
    ) : ContentEntryDto

    @Serializable
    @SerialName("list_item")
    data class ListItem(
        @SerialName("title") val title: String? = null,
        @SerialName("emoji") val emoji: String? = null,
        @SerialName("color") val backgroundColor: String? = null,
        @SerialName("action") val action: ActionDto,
    ) : ContentEntryDto

    @Serializable
    @SerialName("list_item_preview")
    data class ListItemPreview(
        @SerialName("title") val title: String? = null,
        @SerialName("description") val description: String? = null,
        @SerialName("thumb_url") val thumbUrl: String? = null,
        @SerialName("action") val action: ActionDto,
    ) : ContentEntryDto

    @Serializable
    @SerialName("tips")
    data class Tips(
        @SerialName("config") val config: TipsConfigDto? = null,
        @SerialName("content") val tips: List<TipDto>,
    ) : ContentEntryDto

    @Serializable
    @SerialName("shopping_list")
    data class ShoppingList(
        @SerialName("name") val name: String,
        @SerialName("color") val color: String? = null,
        @SerialName("action") val action: ActionDto.ShoppingList,
    ) : ContentEntryDto

    @Serializable
    @SerialName("plain_text")
    data class PlainText(
        @SerialName("text") val text: String,
        @SerialName("style") val style: TextStyleDto? = null,
        @SerialName("insets") val insets: Insets? = null,
    ) : ContentEntryDto

    @Serializable
    @SerialName("line")
    data class Line(
        @SerialName("thickness") val thickness: Int? = null,
        @SerialName("color") val color: String? = null,
        @SerialName("indent") val indent: Int? = null,
        @SerialName("end_indent") val endIndent: Int? = null,
    ) : ContentEntryDto

    @Serializable
    @SerialName("button")
    data class Button(
        @SerialName("text") val text: String,
        @SerialName("height") val height: Int? = null,
        @SerialName("style") val style: TextStyleDto? = null,
        @SerialName("container_color") val containerColor: String? = null,
        @SerialName("action") val action: ActionDto,
        @SerialName("insets") val insets: Insets? = null,
    ) : ContentEntryDto

    @Serializable
    @SerialName("row")
    data class Row(
        @SerialName("weights") val weights: List<Float>? = null,
        @SerialName("content") val content: List<ContentEntryDto>,
        @SerialName("insets") val insets: Insets? = null,
        @SerialName("action") val action: ActionDto? = null,
    ) : ContentEntryDto

    @Serializable
    @SerialName("column")
    data class Column(
        @SerialName("content") val content: List<ContentEntryDto>,
        @SerialName("insets") val insets: Insets? = null,
        @SerialName("action") val action: ActionDto? = null,
    ) : ContentEntryDto

    @Serializable
    @SerialName("space")
    data class Space(
        @SerialName("size") val size: Int,
    ) : ContentEntryDto

    @Serializable
    @SerialName("decorator")
    data class Decorator(
        @SerialName("content") val content: ContentEntryDto,
        @SerialName("color") val backgroundColor: String,
        @SerialName("insets") val insets: Insets? = null,
        @SerialName("corner") val cornerRadius: Int? = null,
        @SerialName("margins") val margins: Insets? = null,
        @SerialName("action") val action: ActionDto? = null,
    ) : ContentEntryDto

    @Serializable
    @SerialName("anchor")
    data class Anchor(
        @SerialName("content") val content: ContentEntryDto,
        @SerialName("anchor_id") val anchorId: String,
    ) : ContentEntryDto

    @Serializable
    @SerialName("sticky_content")
    data class StickyContent(
        @SerialName("content") val content: ContentEntryDto,
    ) : ContentEntryDto

    @Serializable
    @SerialName("horizontal_list")
    data class HorizontalList(
        @SerialName("content") val content: List<ContentEntryDto>,
        @SerialName("content_insets") val contentInsets: Insets? = null,
    ) : ContentEntryDto

    @Serializable
    @SerialName("unit_selector")
    data class UnitSelectorContainer(
        val metric: List<ContentEntryDto>,
        val imperial: List<ContentEntryDto>,
    ) : ContentEntryDto

    @Serializable
    data object Default : ContentEntryDto

    companion object {
        val module = SerializersModule {
            polymorphic(ContentEntryDto::class) {
                subclass(Title::class, Title.serializer())
                subclass(Image::class, Image.serializer())
                subclass(RichText::class, RichText.serializer())
                subclass(Supplement::class, Supplement.serializer())
                subclass(Day::class, Day.serializer())
                subclass(Card::class, Card.serializer())
                subclass(Group::class, Group.serializer())
                subclass(Carousel::class, Carousel.serializer())
                subclass(Header::class, Header.serializer())
                subclass(ImageCard::class, ImageCard.serializer())
                subclass(ListItem::class, ListItem.serializer())
                subclass(ListItemPreview::class, ListItemPreview.serializer())
                subclass(Tips::class, Tips.serializer())
                subclass(ShoppingList::class, ShoppingList.serializer())
                subclass(PlainText::class, PlainText.serializer())
                subclass(RichText::class, RichText.serializer())
                subclass(Line::class, Line.serializer())
                subclass(Button::class, Button.serializer())
                subclass(Row::class, Row.serializer())
                subclass(Column::class, Column.serializer())
                subclass(Space::class, Space.serializer())
                subclass(Decorator::class, Decorator.serializer())
                subclass(Anchor::class, Anchor.serializer())
                subclass(StickyContent::class, StickyContent.serializer())
                subclass(HorizontalList::class, HorizontalList.serializer())
                subclass(UnitSelectorContainer::class, UnitSelectorContainer.serializer())
                defaultDeserializer { Default.serializer() }
            }
        }
    }
}

@Serializable
internal data class CardDto(
    @SerialName("title") val title: String,
    @SerialName("thumb_url") val thumbUrl: String,
    @SerialName("action") val action: ActionDto,
)

@Serializable
internal data class TipDto(
    @SerialName("id") val id: String,
    @SerialName("title") val title: String,
    @SerialName("text") val text: String,
)
